'use strict';

// Vue
import Vue from 'vue';
import router from 'router';

// API
import { HTTP } from 'api/http';
import {
  requestLogin,
  requestRegisterUser,
  requestResetPassword,
  requestChangePassword,
  requestConfirmPassword,
  requestAccountVerification,
  requestResendVerification,
  requestExtendToken,
  requestExtendMdLivetoken,
  requestExtendAnswerToken,
  requestUpdateResponses,
  requestUpdateState,
  requestCurrentUser,
} from 'api/auth';


// 3rd party
import moment from 'moment';

// Services
import { handleF2fSelection } from 'utils/f2fSelection';

// State
const state = {
  activeHeader: '',
  authToken: '',
  user: {},
  redirect: '',
  verified: 'initial',
};

// Getters
const getters = {
  isAuthenticated: state => !!state.authToken,
  user: state => state.user,
  verified: state => state.verified,
  activeHeader: state => state.activeHeader,
  loadingPasswordReset: state => state.loadingPasswordReset,
  tempEmail: state => state.tempEmail,
  logoutMessage: state => state.logoutMessage,
};

// Mutations
const mutations = {
  SET_TOKENS(state, payload) {
    _.forEach(payload, (value, key) => { // eslint-disable-line no-undef
      
      if (key === 'fueledAuthTokenTimestamp' || key === 'mdLiveAuthTokenTimestamp') {
        if(!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, value);
        }
      } else {
        localStorage.setItem(key, value);
      }

      if (key === 'fueledAuthToken') {
        state.authToken = value;
      }
    });
  },

  SET_HEADERS(state, payload) {
    if (payload === 'fueled') {
      HTTP.defaults.headers.common.Authorization = `Token ${localStorage.getItem(
        'fueledAuthToken'
      )}`;

      state.activeHeader = 'fueled';
    }

    if (payload === 'mdLive') {
      HTTP.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem(
        'mdLiveAuthToken'
      )}`;

      state.activeHeader = 'mdLive';
    }

    if (payload === 'answer') {
      HTTP.defaults.headers.common.Authorization = `Token ${localStorage.getItem('answer_token')}`;

      state.activeHeader = 'answer';
    }

    if (payload === 'default') {
      delete HTTP.defaults.headers.common.Authorization;

      state.activeHeader = null;
    }
  },

  SET_USER(state, user) {
    Vue.set(state, 'user', {
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
    });
  },

  RESET_USER(state) {
    Vue.set(state, 'user', {});
  },

  SET_REDIRECT(state, payload) {
    Vue.set(state, 'redirect', payload || '');
  },

  SET_VERIFICATION_STATUS(state, payload) {
    state.verified = payload;
  },

  SAVE_EMAIL_TO_STATE(state, payload) {
    Vue.set(state, 'tempEmail', payload);
  },

  SET_LOADING_STATE(state, payload) {
    Vue.set(state, payload.key, payload.value);
  },

  SET_LOGOUT_MESSAGE(state, payload) {
    Vue.set(state, 'logoutMessage', payload);
  },
};

// Actions
const actions = {
  resetAllData({ dispatch, commit }) {
    commit('SET_TOKENS', {
      fueledAuthToken: '',
    });

    localStorage.removeItem('answer_token');
    localStorage.removeItem('answer_id');
    localStorage.removeItem('singleAnswer');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('updateUser');
    localStorage.removeItem('timezone');
    localStorage.removeItem('fueledAuthToken');
    localStorage.removeItem('mdLiveAuthToken');
    localStorage.removeItem('answerTokenTimestamp');
    sessionStorage.removeItem('fueledAuthTokenTimestamp');
    sessionStorage.removeItem('mdLiveAuthTokenTimestamp');
    localStorage.removeItem('objectWho');
    localStorage.removeItem('appointmentSelectionAnswer');
    localStorage.removeItem('newUser');
    localStorage.removeItem('startIntakeTimeStamp');
    localStorage.removeItem('f2fselected');
    localStorage.removeItem('loginOvertake');
    localStorage.removeItem('upcomingAppts');
    localStorage.removeItem('howContact');
    localStorage.removeItem('appointmentStartingQuestion');
    localStorage.removeItem('rescheduling');
    localStorage.removeItem('rescheduleId');
    localStorage.removeItem('isSafe');
    localStorage.removeItem('safeNextQuestion');
    localStorage.removeItem('childOrg');
    localStorage.removeItem('startingQuestion');
    localStorage.removeItem('answerNextUrl');
    localStorage.removeItem('selectedProvider');
    localStorage.removeItem('fake_user_token');
    localStorage.removeItem('fake_user_id');

    dispatch('resetProviderList');
    dispatch('resetFirstIntake');
    dispatch('resetQuestionnaireData');
    dispatch('resetUser');
    commit('appointment/SAVE_QUERY_DATA', null, { root: true });
  },

  resetFirstIntake() {
    const stored = ['Who', 'Location', 'Complaint', 'SecondaryComplaint'];
    stored.forEach(item => {
      localStorage.removeItem(`object${item}`);
      localStorage.removeItem(`answer${item}`);
    });
  },

  resetQuestionnaireData() {
    // Delete all data related to questionnaires
    _.forEach(_.filter(Object.keys(localStorage), key => key.split('_').shift() === 'q'), questionnaire => { // eslint-disable-line no-undef
      localStorage.removeItem(questionnaire);
    });
  },

  resetDataAfterLogin({ dispatch }) {
    localStorage.removeItem('answer_token');
    localStorage.removeItem('answer_id');
    localStorage.removeItem('answerTokenTimestamp');
    localStorage.removeItem('startIntakeTimeStamp');
    localStorage.removeItem('loginOvertake');
    localStorage.removeItem('singleAnswer');
    localStorage.removeItem('isSafe');
    localStorage.removeItem('safeNextQuestion');
    localStorage.removeItem('newUser');

    dispatch('resetFirstIntake');
    dispatch('resetQuestionnaireData');
  },

  requestLogout({ dispatch }) {
    dispatch('resetAllData');
    router.push({ name: 'logout' });

    setTimeout(()  => {
      // WHY: Forces the log-out
      // TODO: Investigate why this is necessary rather than using $router.push({ name: `home` });
      //window.location = '/';
      router.push({ name: 'home' });
      localStorage.removeItem('cwbAuthToken');
      window.location.reload();
    });
  },

  requestLogin({ commit, dispatch, state }, model) {
    delete HTTP.defaults.headers.common.Authorization;

    if ( !JSON.parse(localStorage.getItem('loginOvertake')) ) dispatch('resetDataAfterLogin');

    return new Promise((resolve, reject) => {
      requestLogin(model)
        .then(response => {
          dispatch('updateUser', response.data || {});


          commit('SET_TOKENS', {
            fueledAuthToken: response.data.auth_token,
            mdLiveAuthToken: response.data.md_live.jwt,
            fueledAuthTokenTimestamp: moment().format(),
            mdLiveAuthTokenTimestamp: moment().format(),
          });

          commit('SET_HEADERS', 'mdLive');
          commit('SET_VERIFICATION_STATUS', true);
          dispatch('storeRedirect', '');
          resolve(response);

          if ( !JSON.parse(localStorage.getItem('loginOvertake')) ) {
            router.push(state.redirect ? { name: state.redirect } : { name: 'home' });
          } else {
            dispatch('resetDataAfterLogin');
            router.push({ name: 'register-success' });
          }

          if (
            localStorage.getItem('startIntakeTimeStamp') &&
            localStorage.getItem('loginOvertake') &&
            localStorage.getItem('singleAnswer')
          ) {
            dispatch('handleLoginOvertake');
          }
        })
        .catch(error => {
          if (error && error.data && error.data.error_type === 'NotVerified') {
            commit('SET_VERIFICATION_STATUS', false);
          }

          console.error(error);
          reject(error);
        });
    });
  },

  handleLoginOvertake({ commit, dispatch, rootGetters }) {
    commit('SET_HEADERS', 'fueled');

    if ( JSON.parse(localStorage.getItem('f2fselected')) ) {

      handleF2fSelection()
        .then(() => {
          dispatch('app/setConfirmMessage', 'Would you like to submit your request for an in person appointment to our team now?', { root: true });
          dispatch('app/setConfirmDescription', 'A member of our team will call you within 1-2 days.', { root: true });
          dispatch('app/setConfirmButton', {
            label: 'Continue',
            action: 'appointment/requestScheduleAppointment',
          }, { root: true });

          dispatch('requestUpdateResponses', 'confirm');
        })
        .catch(error => {
          console.error(error);
        });

      return;
    }

    else if(rootGetters['appointment/queryData']) {
      dispatch('appointment/requestAppointmentQuery', rootGetters['appointment/queryData'], { root: true });
    }

    dispatch('requestUpdateResponses');
  },

  requestUpdateResponses({ dispatch, rootGetters }, redirectTarget) {
    const registerTarget = rootGetters['appointment/queryData'] ? 'register-success' : 'register';

    requestUpdateResponses(localStorage.getItem('singleAnswer'))
      .then(() => {
        dispatch('resetDataAfterLogin');
        router.push({ name: redirectTarget || registerTarget });
      })
      .catch(error => {
        console.error(error);
      });
  },

  requestResetPassword({ commit }, model) {
    return new Promise((resolve, reject) => {
      requestResetPassword(model)
        .then(response => {
          commit('SET_VERIFICATION_STATUS', true);
          commit('SAVE_EMAIL_TO_STATE', model.email);
          resolve(response);
        })
        .catch(error => {
          console.error(error);
          commit('SET_VERIFICATION_STATUS', false);
          commit('SAVE_EMAIL_TO_STATE', model.email);
          reject(error);
        });
    });
  },

  requestChangePassword({ commit }, model) {
    commit('auth/SET_HEADERS', 'fueled', { root: true });

    return new Promise((resolve, reject) => {
      requestChangePassword(model)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  requestUpdateState({ commit }, model) {
    commit('auth/SET_HEADERS', 'fueled', { root: true });

    return new Promise((resolve, reject) => {
      requestUpdateState(model)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  requestConfirmPassword({ commit }, model) {
    return new Promise((resolve, reject) => {
      requestConfirmPassword(model)
        .then(response => {
          commit('SET_LOADING_STATE', {
            key: 'loadingPasswordReset',
            value: true,
          });

          setTimeout(() => {
            commit('SET_LOADING_STATE', {
              key: 'loadingPasswordReset',
              value: false,
            }, 3000);
          });

          resolve(response);
        })
        .catch(error => {
          console.error(error);
          return reject(error);
        });
    });
  },

  requestCurrentUser({ state, commit }, { auth_token, redirect }) {
    if (!state.auth_token) {
      commit('SET_TOKENS', {
        fueledAuthToken: auth_token,
      });

      commit('SET_HEADERS', 'fueled');

      if (redirect) {
        const path = state.redirect;
        router.push(path ? { path } : { name: 'Home' });
      }
    }
  },

  requestCurrentProfile({ commit, dispatch }) {
    commit('auth/SET_HEADERS', 'fueled', { root: true });


    return new Promise((resolve, reject) => {
      requestCurrentUser()
        .then((response) => {
          dispatch('updateUser', response.data || {});
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  },


  requestRegisterUser({ commit, dispatch, rootGetters }, model) {
    return new Promise((resolve, reject) => {
      delete HTTP.defaults.headers.common.Authorization;

      model.answer = localStorage.getItem('singleAnswer');
      model.relationship = 'Self';

      if (
        localStorage.getItem('answerComplaint') &&
        localStorage.getItem('answerSecondaryComplaint')
      ) {
        model.chief_complaint1 = localStorage.getItem('answerComplaint');
        model.chief_complaint2 = localStorage.getItem('answerSecondaryComplaint');
      } else {
        model.chief_complaint1 = localStorage.getItem('answerComplaint');
      }

      if (localStorage.getItem('childOrg')) {
        model.organisation = localStorage.getItem('childOrg');
      } else if (rootGetters['app/org'] && rootGetters['app/org'].id) {
        model.organisation = rootGetters['app/org'].id;
      }

      if (localStorage.getItem('answerLocation')) {
        model.appointment_state = localStorage.getItem('answerLocation');
      }

      if (localStorage.getItem('timezone') !== null) {
        model.timezone = localStorage.getItem('timezone');
      } else {
        model.timezone = 'America/New_York';
      }

      if (model.address2 === '' || model.address2 === null || model.address2 === undefined) {
        model.address2 = null;
      }

      requestRegisterUser(model)
        .then(response => {
          dispatch('updateUser', response.data || {});
          localStorage.setItem('newUser', true);

          commit('SET_TOKENS', {
            fueledAuthToken: response.data.auth_token,
            mdLiveAuthToken: response.data.md_live.jwt,
            fueledAuthTokenTimestamp: moment().format(),
            mdLiveAuthTokenTimestamp: moment().format(),
          });

          commit('SET_HEADERS', 'mdLive');

          if ( !rootGetters['questions/f2fSelected'] && !rootGetters['appointment/queryData'] ) {
            dispatch(
              'appointment/requestScheduleAppointment',
              {
                ...model,
                patientId: response.data.mdlive_id,
                newUser: true,
              },
              {
                root: true,
              }
            );
          }
          else if (rootGetters['appointment/queryData']) {

            const model = rootGetters['appointment/queryData'];
            dispatch(
              'appointment/requestAppointmentQuery',
              {
                ...model,
              },
              {
                root: true,
              }
            );
          } else {
            _.forEach(_.filter(Object.keys(localStorage), key => key.split('_').shift() === 'q'), questionnaire => { // eslint-disable-line no-undef
              localStorage.removeItem(questionnaire);
            });

            localStorage.removeItem('appointmentSelectionAnswer');
          }

          router.push({
            name: 'register-verification',
          });
          resolve(response);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  updateUser({ commit }, data) {
    commit('SET_USER', data);
    localStorage.setItem('updateUser', JSON.stringify(data));
  },

  requestAccountVerification({ commit, rootGetters }, payload) {
    // eslint-disable-line no-unused-vars
    delete HTTP.defaults.headers.common.Authorization;

    if (payload) {
      localStorage.removeItem('singleAnswer');

      const otp = payload.otp.toString();

      if ( otp.length < 6 ) {
        const zerosToAdd = 6 - otp.length;
        const splitOtp = otp.split('');

        for (let i = 0; i < zerosToAdd; i++) {
          splitOtp.unshift('0');
        }

        payload.otp = splitOtp.join('');
      } else {
        payload.otp = payload.otp.toString();
      }

      if (!payload.email && rootGetters['auth/tempEmail']) {
        payload.email = rootGetters['auth/tempEmail'];
      }

      return new Promise((resolve, reject) => {
        requestAccountVerification(payload).then((response) => {
          commit('SET_VERIFICATION_STATUS', true);
          commit('SET_HEADERS', 'mdLive');

          resolve(response);
        }).catch(error => {
          reject(error);
        });
      });
    }
  },

  requestResendVerification({ commit }, payload) { // eslint-disable-line no-unused-vars
    if (payload) {
      return requestResendVerification(payload);
    }
  },

  requestExtendToken({ commit, dispatch }) {
    const token = localStorage.getItem('fueledAuthToken');

    if (token) {
      return new Promise((resolve, reject) => {
        requestExtendToken(token)
          .then(response => {
            commit('SET_TOKENS', {
              fueledAuthToken: response.data.auth_token,
              fueledAuthTokenTimestamp: moment().format(),
            });

            resolve(response);
          })
          .catch(error => {
            console.error('Error extending token', error);
            reject(error);
            dispatch('setLogoutMessage', 'sessionExpired');
          });
      });
    }
  },

  requestExtendMdLiveToken({ commit, dispatch }) {
    const token = localStorage.getItem('mdLiveAuthToken');

    if (token) {
      return new Promise((resolve, reject) => {
        requestExtendMdLivetoken(token)
          .then(response => {
            commit('SET_TOKENS', {
              mdLiveAuthToken: response.data.jwt_token,
              mdLiveAuthTokenTimestamp: moment().format(),
            });

            resolve(response);
          })
          .catch(error => {
            console.error('Error extending mdLive token', error);
            reject(error);
            dispatch('setLogoutMessage', 'sessionExpired');
          });
      });
    }
  },

  requestExtendAnswerToken({ commit, dispatch }) {
    const token = localStorage.getItem('answer_token');

    if ( token ) {
      return new Promise((resolve, reject) => {
        requestExtendAnswerToken()
          .then(response => {
            commit('SET_TOKENS', {
              answer_token: response.data.answer_token,
              answerTokenTimestamp: moment().format(),
            });

            return resolve(response);
          })
          .catch(error => {
            console.error('Error extending answer token', error);
            dispatch('setLogoutMessage', 'sessionExpired');
            return reject(error);
          });
      });
    }
  },


  resetUser({ commit }) {
    commit('RESET_USER');
  },

  resetProviderList({ dispatch }) {
    dispatch('messaging/clearConversations', null, { root: true });
  },

  storeRedirect({ commit }, path) {
    commit('SET_REDIRECT', path);
  },

  setLogoutMessage({ commit }, payload) {
    commit('SET_LOGOUT_MESSAGE', payload);
    router.push({ name: 'logout' });
    localStorage.setItem('forcedLogout', true);
  },
};

// Export
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./client/store/auth.js